import { isProductionHostname } from "@/analytics/shouldEnableAnalytics";

/**
 * The canonical Production base URL - used ONLY when the current page is
 * actually being served from the real production hostname
 * (`isProductionHostname`, the same strict allowlist `shouldEnableAnalytics`
 * uses). Deliberately never used to build a link on any other host - a
 * Deploy Preview or local dev build instead shares a link back to ITS OWN
 * origin (see `buildChallengeUrl` below), so testing the whole share/open
 * flow on a Deploy Preview never fabricates a `snakescore.com` URL that
 * would 404/point at the wrong environment's data.
 */
const CANONICAL_PRODUCTION_BASE_URL = "https://snakescore.com";

export const CHALLENGE_ROUTE_PREFIX = "/c/";

/**
 * The base URL a freshly-created challenge link should be built against.
 * On the real production hostname this is always the canonical
 * `https://snakescore.com`, never whatever `window.location.origin` happens
 * to report (avoids ever publishing a `www.` vs. bare-domain, or a
 * `http://` vs. `https://`, inconsistency). On every other host (Deploy
 * Preview, local dev) it's that host's own current origin - a safe way to
 * exercise the full share → open flow without ever generating a link that
 * looks like, or could be mistaken for, a real Production share.
 */
export function resolveChallengeBaseUrl(hostname: string | null | undefined, currentOrigin: string): string {
  return isProductionHostname(hostname) ? CANONICAL_PRODUCTION_BASE_URL : currentOrigin;
}

/** The full, canonical shareable URL for one challenge's `public_id`. Never includes a query string or any other identifying data - the path segment (a random v4 UUID) is the entire payload. */
export function buildChallengeUrl(baseUrl: string, publicId: string): string {
  return `${baseUrl.replace(/\/+$/, "")}${CHALLENGE_ROUTE_PREFIX}${publicId}`;
}

/** The suggested share text from the PR spec, parameterized on the actual Daily Points scored - never includes the visitor's/creator's name (the link's own landing page is where that's shown, safely escaped and truncated). */
export function buildShareText(dailyPoints: number): string {
  return `I scored ${dailyPoints.toLocaleString("en-US")} Daily Points in SnakeScore 🐍\nCan you beat me before today's challenge ends?`;
}

/**
 * Whether the Share/CHALLENGE A FRIEND/COPY LINK entry points should be
 * rendered at all - gates the three call sites in `DailyChallengeScene`/
 * `DailyResultScene`/`ScoreChallengeScene` that open `ShareChallengeModal`.
 * CrazyGames-only: that build has no server route for the generated
 * `/c/<id>` link to resolve to (CrazyGames doesn't host it), so
 * `resolveChallengeBaseUrl` above correctly never fabricates a
 * `snakescore.com` link there, but the resulting same-origin link is dead
 * either way - the feature is hidden entirely rather than shipping a
 * broken CTA.
 *
 * Read once via Vite's own build-mode env mechanism
 * (`VITE_DISABLE_SHARING`, set only by `.env.crazygames` - see that file),
 * never `window.location.hostname` at runtime - same "build-time config,
 * not a runtime host sniff" precedent `VITE_FUNCTIONS_BASE_URL` already
 * set. Unset (the normal snakescore.com build) means enabled - identical
 * to today's behavior, unchanged.
 */
export function isSharingEnabled(): boolean {
  return import.meta.env.VITE_DISABLE_SHARING !== "true";
}
